/**
 * Admin API client. Login is unauthenticated — it exchanges the seeded admin
 * email and password for the admin key. Every other call carries that key as
 * the {@code X-Admin-Key} header (read from sessionStorage), which the
 * backend's AdminApiKeyFilter validates. Admin tables fetch a large page and
 * paginate / sort / filter on the client, so the surface here stays small:
 * list, the CRUD and bulk endpoints, the front page, members, comment
 * moderation, and the re-index.
 */

import { apiRequest, http } from "./client";
import type {
  AdminSession,
  BlogPost,
  BlogPostRequest,
  BlogPostSummary,
  BulkUpdateRequest,
  BulkUpdateResult,
  CommentAdminDto,
  CommentAdminRequest,
  CrawlerStatus,
  ExternalResource,
  ExternalResourceRequest,
  FrontPage,
  IndexResult,
  Member,
  MemberCreateRequest,
  MemberUpdateRequest,
  PageResponse,
  Photo,
  PhotoRequest,
  UploadResponse,
  Video,
  VideoLookup,
  VideoRequest,
} from "./types";

const KEY_STORAGE = "csmckay_admin_key";

/** Large page size: admin tables load everything once, then page client-side. */
const ALL = 500;

/** Session-scoped storage for the admin key (cleared when the tab closes). */
export const adminKey = {
  get(): string | null {
    try {
      return sessionStorage.getItem(KEY_STORAGE);
    } catch {
      return null;
    }
  },
  set(key: string): void {
    try {
      sessionStorage.setItem(KEY_STORAGE, key);
    } catch {
      /* storage unavailable — key simply won't persist across reloads */
    }
  },
  clear(): void {
    try {
      sessionStorage.removeItem(KEY_STORAGE);
    } catch {
      /* ignore */
    }
  },
};

/** The auth header for the currently stored key. */
function headers(): HeadersInit {
  return { "X-Admin-Key": adminKey.get() ?? "" };
}

export const adminApi = {
  // ---------------------------------------------------------------- session
  /** Trade credentials for the admin key (200 → valid, throws ApiError otherwise). */
  login: (email: string, password: string) =>
    http.post<AdminSession>("/api/admin/login", { email, password }),

  /**
   * Confirm the stored key is still valid (used to restore a session). The
   * server also re-issues the admin session cookie if the browser lost it —
   * that cookie is what lets <img> tags load private photos in the console.
   */
  whoami: () => http.get<AdminSession>("/api/admin/whoami", headers()),

  /** End the admin's cookie session (the key itself is forgotten client-side). */
  logout: () => http.post<void>("/api/session/logout", {}),

  // ---------------------------------------------------------------- crawler
  reindex: () => http.post<IndexResult>("/api/crawlers/local", {}, headers()),
  crawlerStatus: () => http.get<CrawlerStatus>("/api/crawlers/local", headers()),

  // ----------------------------------------------------------------- photos
  listPhotos: () =>
    http.get<PageResponse<Photo>>(`/api/photos?page=0&size=${ALL}`, headers()),
  createPhoto: (body: PhotoRequest) => http.post<Photo>("/api/photos", body, headers()),
  updatePhoto: (id: number, body: PhotoRequest) =>
    http.put<Photo>(`/api/photos/${id}`, body, headers()),
  deletePhoto: (id: number) => http.del<void>(`/api/photos/${id}`, headers()),
  bulkPhotos: (body: BulkUpdateRequest) =>
    http.post<BulkUpdateResult>("/api/photos/bulk", body, headers()),

  // ----------------------------------------------------------------- videos
  listVideos: () =>
    http.get<PageResponse<Video>>(`/api/videos?page=0&size=${ALL}`, headers()),
  createVideo: (body: VideoRequest) => http.post<Video>("/api/videos", body, headers()),
  updateVideo: (id: number, body: VideoRequest) =>
    http.put<Video>(`/api/videos/${id}`, body, headers()),
  deleteVideo: (id: number) => http.del<void>(`/api/videos/${id}`, headers()),
  bulkVideos: (body: BulkUpdateRequest) =>
    http.post<BulkUpdateResult>("/api/videos/bulk", body, headers()),
  /** Platform, title, thumbnail… for a pasted video link (best-effort). */
  lookupVideo: (url: string) =>
    http.get<VideoLookup>(`/api/admin/video-lookup?url=${encodeURIComponent(url)}`, headers()),

  // ------------------------------------------------------------- front page
  getFrontPage: () => http.get<FrontPage>("/api/admin/front-page", headers()),
  saveFrontPage: (photoIds: number[], videoIds: number[]) =>
    http.put<FrontPage>("/api/admin/front-page", { photoIds, videoIds }, headers()),

  // ---------------------------------------------------------------- members
  listMembers: () => http.get<Member[]>("/api/admin/members", headers()),
  createMember: (body: MemberCreateRequest) =>
    http.post<Member>("/api/admin/members", body, headers()),
  updateMember: (id: number, body: MemberUpdateRequest) =>
    http.put<Member>(`/api/admin/members/${id}`, body, headers()),
  deleteMember: (id: number) => http.del<void>(`/api/admin/members/${id}`, headers()),

  // -------------------------------------------------------------- references
  listResources: () =>
    http.get<PageResponse<ExternalResource>>(`/api/resources?page=0&size=${ALL}`, headers()),
  createResource: (body: ExternalResourceRequest) =>
    http.post<ExternalResource>("/api/resources", body, headers()),
  updateResource: (id: number, body: ExternalResourceRequest) =>
    http.put<ExternalResource>(`/api/resources/${id}`, body, headers()),
  deleteResource: (id: number) => http.del<void>(`/api/resources/${id}`, headers()),

  // -------------------------------------------------------------- journal
  listPosts: () =>
    http.get<PageResponse<BlogPostSummary>>(`/api/blog/admin/posts?page=0&size=${ALL}`, headers()),
  getPost: (id: number) => http.get<BlogPost>(`/api/blog/admin/posts/${id}`, headers()),
  createPost: (body: BlogPostRequest) => http.post<BlogPost>("/api/blog/posts", body, headers()),
  updatePost: (id: number, body: BlogPostRequest) =>
    http.put<BlogPost>(`/api/blog/posts/${id}`, body, headers()),
  deletePost: (id: number) => http.del<void>(`/api/blog/posts/${id}`, headers()),

  // ---------------------------------------------------------------- uploads
  /**
   * Upload a media file (image or video) and get back its public URL, for use
   * as a photo/video/cover URL. Sent as multipart/form-data with the admin key;
   * Content-Type is intentionally left to the browser so the boundary is set.
   */
  uploadFile: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiRequest<UploadResponse>("/api/uploads", {
      method: "POST",
      headers: headers(),
      body: form,
    });
  },

  // ---------------------------------------------------------------- comments
  listComments: () => http.get<CommentAdminDto[]>("/api/comments/admin", headers()),
  updateComment: (id: number, body: CommentAdminRequest) =>
    http.put<CommentAdminDto>(`/api/comments/${id}`, body, headers()),
  deleteComment: (id: number) => http.del<void>(`/api/comments/${id}`, headers()),
};
